import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import type { Supplier } from './useCompanySuppliers';
import type { InventoryRecord } from './useCompanyInventory';

export interface POItem {
  id?: number;
  ingredient: number;
  ingredient_name: string;
  ingredient_unit: string;
  quantity_ordered: string;
  unit_price: string;
  quantity_received?: string;
}

export interface PurchaseOrder {
  id: number;
  supplier: number | null;
  supplier_name: string;
  status: string;
  status_display: string;
  expected_delivery_date: string | null;
  received_date: string | null;
  notes: string;
  items: POItem[];
  total_value: string;
  created_at: string;
  updated_at: string;
}

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  confirmed: 'Confirmed',
  received: 'Received',
  cancelled: 'Cancelled',
};

export const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft:     { bg: '#f1f5f9', color: '#475569' },
  sent:      { bg: '#dbeafe', color: '#1d4ed8' },
  confirmed: { bg: '#dcfce7', color: '#166534' },
  received:  { bg: '#f0fdf4', color: '#166534' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

export function useCompanyPurchaseOrders() {
  const { isExpired } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [ingredients, setIngredients] = useState<InventoryRecord[]>([]);  // we re-use inventory shape for ingredient list
  const [allIngredients, setAllIngredients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  // New / Edit order form
  const [showOrderForm, setShowOrderForm] = useState(searchParams.get('tab') === 'new' || false);
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null);
  const [orderForm, setOrderForm] = useState({
    supplier: '',
    expected_delivery_date: '',
    notes: '',
    status: 'draft',
  });
  const [orderItems, setOrderItems] = useState<{ ingredient: string; ingredient_name: string; ingredient_unit: string; quantity_ordered: string; unit_price: string }[]>([]);
  const [saving, setSaving] = useState(false);

  // Receive delivery modal
  const [receiveModal, setReceiveModal] = useState<{
    show: boolean; order: PurchaseOrder | null;
    receivedQtys: Record<number, string>; saving: boolean;
  }>({ show: false, order: null, receivedQtys: {}, saving: false });

  // Delete
  const [deleteState, setDeleteState] = useState<{
    showModal: boolean; order: PurchaseOrder | null; deleting: boolean;
  }>({ showModal: false, order: null, deleting: false });

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, suppliersRes, ingredientsRes] = await Promise.all([
        companyAPI.getPurchaseOrders(),
        companyAPI.getSuppliers(),
        companyAPI.getIngredients(),
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setAllIngredients(ingredientsRes.data);
    } catch (err) {
      console.error('Failed to fetch purchase orders data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = orders.filter(o => statusFilter === 'all' || o.status === statusFilter);

  // Supplier-scoped ingredient list (use all ingredients)
  const availableIngredients = allIngredients;

  const openCreate = () => {
    setEditingOrder(null);
    setOrderForm({ supplier: '', expected_delivery_date: '', notes: '', status: 'draft' });
    setOrderItems([]);
    setFeedback({ error: '', success: '' });
    setShowOrderForm(true);
  };

  const openEdit = (order: PurchaseOrder) => {
    setEditingOrder(order);
    setOrderForm({
      supplier: order.supplier ? String(order.supplier) : '',
      expected_delivery_date: order.expected_delivery_date ?? '',
      notes: order.notes,
      status: order.status,
    });
    setOrderItems(order.items.map(i => ({
      ingredient: String(i.ingredient),
      ingredient_name: i.ingredient_name,
      ingredient_unit: i.ingredient_unit,
      quantity_ordered: String(i.quantity_ordered),
      unit_price: i.unit_price ?? '',
    })));
    setFeedback({ error: '', success: '' });
    setShowOrderForm(true);
  };

  const addItem = () => {
    setOrderItems(prev => [...prev, { ingredient: '', ingredient_name: '', ingredient_unit: '', quantity_ordered: '', unit_price: '' }]);
  };

  const removeItem = (index: number) => {
    setOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: string, value: string) => {
    setOrderItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'ingredient' && value) {
        const ing = allIngredients.find((i: any) => String(i.id) === value);
        if (ing) {
          updated[index].ingredient_name = ing.name;
          updated[index].ingredient_unit = ing.unit;
          if (!updated[index].unit_price && ing.cost_per_unit) {
            updated[index].unit_price = ing.cost_per_unit;
          }
        }
      }
      return updated;
    });
  };

  const handleSaveOrder = async () => {
    if (!orderForm.supplier) {
      setFeedback({ error: 'Please select a supplier.', success: '' });
      return;
    }
    if (orderItems.length === 0) {
      setFeedback({ error: 'Add at least one line item.', success: '' });
      return;
    }
    const invalidItem = orderItems.find(i => !i.ingredient || !i.quantity_ordered);
    if (invalidItem) {
      setFeedback({ error: 'Each line item must have an ingredient and quantity.', success: '' });
      return;
    }
    setSaving(true);
    setFeedback({ error: '', success: '' });
    const payload = {
      supplier: parseInt(orderForm.supplier),
      expected_delivery_date: orderForm.expected_delivery_date || null,
      notes: orderForm.notes,
      status: orderForm.status,
      items: orderItems.map(i => ({
        ingredient: parseInt(i.ingredient),
        quantity_ordered: i.quantity_ordered,
        unit_price: i.unit_price !== '' ? i.unit_price : null,
      })),
    };
    try {
      if (editingOrder) {
        const res = await companyAPI.updatePurchaseOrder(editingOrder.id, payload);
        setOrders(prev => prev.map(o => o.id === editingOrder.id ? res.data : o));
        setFeedback({ error: '', success: 'Order updated.' });
      } else {
        const res = await companyAPI.createPurchaseOrder(payload);
        setOrders(prev => [res.data, ...prev]);
        setFeedback({ error: '', success: 'Order created.' });
      }
      setShowOrderForm(false);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to save order.';
      setFeedback({ error: msg, success: '' });
    } finally {
      setSaving(false);
    }
  };

  const openReceive = (order: PurchaseOrder) => {
    const qtys: Record<number, string> = {};
    order.items.forEach(i => { if (i.id) qtys[i.id] = String(i.quantity_ordered); });
    setReceiveModal({ show: true, order, receivedQtys: qtys, saving: false });
  };

  const handleReceive = async () => {
    if (!receiveModal.order) return;
    setReceiveModal(prev => ({ ...prev, saving: true }));
    const items = Object.entries(receiveModal.receivedQtys).map(([id, qty]) => ({
      id: parseInt(id),
      quantity_received: parseFloat(qty),
    }));
    try {
      const res = await companyAPI.receivePurchaseOrder(receiveModal.order.id, { items });
      setOrders(prev => prev.map(o => o.id === receiveModal.order!.id ? res.data : o));
      setReceiveModal({ show: false, order: null, receivedQtys: {}, saving: false });
      setFeedback({ error: '', success: 'Delivery received. Stock updated.' });
    } catch {
      setReceiveModal(prev => ({ ...prev, saving: false }));
      setFeedback({ error: 'Failed to receive order.', success: '' });
    }
  };

  const handleDelete = async () => {
    if (!deleteState.order) return;
    setDeleteState(prev => ({ ...prev, deleting: true }));
    try {
      await companyAPI.deletePurchaseOrder(deleteState.order.id);
      setOrders(prev => prev.filter(o => o.id !== deleteState.order!.id));
      setDeleteState({ showModal: false, order: null, deleting: false });
      setFeedback({ error: '', success: 'Order deleted.' });
    } catch {
      setDeleteState(prev => ({ ...prev, deleting: false }));
      setFeedback({ error: 'Failed to delete order.', success: '' });
    }
  };

  return {
    isExpired,
    orders,
    suppliers,
    allIngredients,
    loading,
    statusFilter,
    setStatusFilter,
    filtered,
    feedback,
    setFeedback,
    showOrderForm,
    setShowOrderForm,
    editingOrder,
    orderForm,
    setOrderForm,
    orderItems,
    saving,
    receiveModal,
    setReceiveModal,
    deleteState,
    setDeleteState,
    openCreate,
    openEdit,
    addItem,
    removeItem,
    updateItem,
    handleSaveOrder,
    openReceive,
    handleReceive,
    handleDelete,
  };
}
